import React, { useEffect, useMemo, useState } from 'react';
import { createContext, ReactElement } from "react";
import { Actions } from '../services/react-query/permissions';
import usePermissionAllowed from '../hooks/usePermissionAllowed';
import { useGetCurrentOrganization } from '../services/react-query/organization';
import _ from 'lodash';

type SelfDisable = (reason: 'archived' | 'other', value: boolean) => void;

type DisableReasons = {
  payment: boolean;
  permission: boolean;
  archived?: boolean;
  other?: boolean;
}

const DEFAULT_REASONS: DisableReasons = {
  payment: false,
  permission: false,
};

type DisableContextType = {
  disabled: boolean;
  reasons: DisableReasons;
  selfDisable: SelfDisable,
}

export const DisableContext = createContext<DisableContextType>({ disabled: false, reasons: DEFAULT_REASONS, selfDisable: () => null });

interface IDisableContextProviderProps {
  permission: Actions;
  children: ReactElement;
}

export default function DisableContextProvider({ permission, children }: IDisableContextProviderProps) {
  const [reasons, setReasons] = useState(DEFAULT_REASONS);
  const disabled = _.some(_.values(reasons));

  const { data: organization } = useGetCurrentOrganization({ enabled: !disabled });
  const permissionAllowed = usePermissionAllowed(permission);

  useEffect(() => {
    const newReasons = _.cloneDeep(reasons);
    if (organization) {
      newReasons.payment = _.includes(['paused', 'canceled'], organization?.subscription_status);
    }

    if (permissionAllowed !== null) {
      newReasons.permission = !permissionAllowed;
    }

    setReasons(newReasons);
  }, [organization, permissionAllowed]);

  const selfDisable: SelfDisable = (reason, value) => setReasons(reasons => {
    if (reasons[reason] === value) return reasons;
    return {
      ...reasons,
      [reason]: value,
    };
  });

  return (
    <DisableContext.Provider value={{ disabled, selfDisable, reasons }}>
      {children}
    </DisableContext.Provider>
  );
}

export function disableWrappedPage(Page: React.FC, permission: Actions) {
  return (
    <DisableContextProvider permission={permission}>
      <Page />
    </DisableContextProvider>
  );
}
